/**
 * AppHeader — the DARK top panel, mirroring skySign's header.tsx. A fixed, full-width #242424 bar
 * (`fixed top-0 left-0 right-0 z-[999]`, `h-12 text-white shadow-md`). Left: a logo area sized to the
 * sidebar width (`w-65 px-4 shrink-0`, 260px) holding the skyCare lockup — an inline circular Skypoint S
 * mark + "skyCare" wordmark in white (NOT the old amber-square placeholder). Right cluster
 * (`flex justify-end flex-1 gap-3 px-4`): a static instance chip + a profile menu only. There is NO
 * notifications bell in V1 — skyCare never renders an affordance not backed by a real service/data.
 *
 * In basic mode the credential is baked into the bundle (VITE_BASIC_AUTH) and re-sent on every
 * request, so there is NO in-app session to end - a "Sign out" button could not actually clear it and
 * would be a dead/dishonest control (a security-trust concern). So the basic-mode profile menu HIDES
 * Sign out and the old no-op "View account", and shows a read-only "signed in as" identity panel.
 *
 * In b2c mode (VITE_AUTH_MODE=b2c) there IS a real MSAL session: the profile menu renders a working
 * "Sign out" (MSAL logoutRedirect) and "Signed in as {account}" from the MSAL account. The menu
 * variant is chosen by the bundle-constant auth mode, so each component calls hooks consistently.
 * Nothing here fetches or renders PHI - it is pure app chrome.
 */
import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import {
  SkyAvatar,
  SkyMenu,
  type SkyMenuContent,
} from "@skypointcloud/skypoint-ui";
import { ChevronDown } from "lucide-react";
import skypointMark from "../assets/skypoint-mark.svg";
import { useSession } from "../lib/session";
import { isB2cMode } from "../auth/authMode";

// Lazy so MSAL (pulled in transitively by the b2c menu) is NEVER bundled in basic mode — mirrors
// main.tsx's dynamic import of the b2c shell. Only loaded when VITE_AUTH_MODE=b2c.
const B2cProfileMenu = lazy(() =>
  import("../auth/B2cProfileMenu").then((m) => ({ default: m.B2cProfileMenu })),
);

/** Circular Skypoint S mark (ASSET, no raw hex in the component) + "skyCare" wordmark, white-on-dark
 *  per the skypoint-branding logo lockup. The product wordmark stays white on the dark header. */
function SkyCareLogo() {
  return (
    <span className="flex items-center gap-2">
      <img src={skypointMark} alt="" aria-hidden="true" width={26} height={26} className="shrink-0" />
      <span className="text-[15px] font-semibold tracking-tight text-white">skyCare</span>
    </span>
  );
}

/** Static, non-interactive instance/environment chip (skySign's workspace badge analogue). */
function InstanceChip() {
  return (
    <span className="hidden items-center rounded-md border border-white/25 bg-white/10 px-2.5 py-1 text-xs font-medium text-white/90 lg:inline-flex">
      skyCare · Sandbox
    </span>
  );
}

/** Profile menu: avatar + name + dropdown. Shows the authenticated principal (from /api/v1/me) when a
 *  session is present, falling back to "Care Manager" otherwise. Under bundled basic-auth there is no
 *  session to end, so the menu is a READ-ONLY identity panel (no dead Sign out / View account actions):
 *  a single non-interactive item naming the signed-in person + how the session is managed. */
function ProfileMenu() {
  const { principal } = useSession();
  const displayName = principal?.name ?? "Care Manager";
  const identityDetail = principal?.email
    ? `${principal.email}${principal.role ? ` · ${principal.role}` : ""}`
    : "Session managed by your environment sign-in";
  const items: SkyMenuContent[] = [
    {
      key: "identity",
      label: `Signed in as ${displayName}`,
      description: identityDetail,
      disabled: true,
    },
  ];

  return (
    <SkyMenu
      align="end"
      trigger={
        <button
          type="button"
          aria-label="Account menu"
          className="flex items-center gap-2 rounded-md px-1.5 py-1 text-white hover:bg-white/10"
        >
          <SkyAvatar name={displayName} size={28} />
          <span className="hidden text-sm font-medium text-white sm:inline">{displayName}</span>
          <ChevronDown className="h-4 w-4 text-white/70" />
        </button>
      }
      items={items}
    />
  );
}

/** Inert placeholder shown for the brief moment the lazy b2c profile menu chunk is loading. */
function ProfileMenuFallback() {
  return (
    <span className="flex items-center gap-2 px-1.5 py-1" aria-hidden="true">
      <SkyAvatar name="Care Manager" size={28} />
    </span>
  );
}

export function AppHeader() {
  return (
    <header className="fixed left-0 right-0 top-0 z-[999]">
      {/* M4: <header> already implies role="banner" — no explicit role here (one banner landmark). */}
      <div className="flex h-12 items-center bg-[#242424] text-white shadow-md">
        {/* Logo area — aligned to the 260px sidebar width */}
        <div className="flex h-full w-65 shrink-0 items-center px-4">
          <Link to="/" className="flex items-center" aria-label="skyCare home">
            <SkyCareLogo />
          </Link>
        </div>
        {/* Right cluster — instance chip + profile only (H1: no notifications bell in V1; we never
            render an affordance not backed by a real service). Still mirrors skySign's header shape. */}
        <div className="flex flex-1 items-center justify-end gap-3 px-4">
          <InstanceChip />
          {isB2cMode() ? (
            <Suspense fallback={<ProfileMenuFallback />}>
              <B2cProfileMenu />
            </Suspense>
          ) : (
            <ProfileMenu />
          )}
        </div>
      </div>
    </header>
  );
}
